import React from 'react';
import { render } from 'react-dom';
import Button, { outline } from '@narvar/nth-button';
import Spinner from '@narvar/nth-spinner';
import List from '@narvar/nth-list';
import Alert from '@narvar/nth-alert';
import ReturnsSidebar from '../../react-components/ReturnsSidebar/ReturnsSidebar';
import ButtonMenu from '../../react-components/ButtonMenu';
import DeleteIcon from '../ImageUploader/components/DeleteIcon';
import EditIcon from '../ImageUploader/components/EditIcon';
import SidebarItemDetails from '../../react-components/SidebarItemDetails/SidebarItemDetails';
import SidebarSectionTitle from '../../react-components/SidebarSectionTitle/SidebarSectionTitle';
import SidebarHTMLContent from '../../react-components/SidebarHTMLContent/SidebarHTMLContent';
import s from './repairSidebar.module.css';
import { store } from '../../store';
import RepairTextAreaComponent from './RepairTextAreaComponent';

export const renderSidebar = ({
  actions,
  checkRequiredFields,
  buttonMenuItems,
  checkCommentField,
  reasonSelected,
  buildFormState,
  checkDisabledSection,
  retakePhoto,
  maxCommentLength,
  sidebarAttrs,
  customerFileUpload,
  sidebarState,
  node,
}) => {
  const {
    restrictions: { itemLimit, maxItemsReached },
    language,
  } = store.getState();
  const { config, open, loading, panel, currentItem, savedItems } = sidebarState;
  const lastEnabledSection = currentItem ? currentItem.lastEnabledSection : null;
  const currentPanel = config.panels[panel];
  const currentItemId = currentItem && currentItem.itemId;
  const itemCategory = currentItem && currentItem.itemCategory;
  const dynamicSections = currentItem && currentItem.dynamicSections;
  const savedItem = savedItems[currentItemId];
  const disableSections = lastEnabledSection !== null;
  const commentEmpty = checkCommentField();
  const numItemsSelected = Object.keys(savedItems).filter(
    (itemId) => parseInt(itemId, 10) !== currentItemId,
  ).length;
  const additionalItemsAvailable = numItemsSelected + 1 === itemLimit;
  let unrenderedSectionCount = 0;

  const SidebarSection = ({ title, index, components, disabled, id }) => {
    const { currentItem } = sidebarState;
    const { repairReasons, multipleSelectReasons } = currentItem;
    const titleMarkup = title ? (
      <SidebarSectionTitle index={index} title={`${index + 1}. ${title}`} disabled={disabled} />
    ) : null;

    const componentsMarkup = components.map((component) => {
      const componentKey = btoa(Math.random());
      const {
        name,
        inputKey,
        required,
        panel: panelLink,
        content,
        center,
        text,
        itemPropertiesCondition,
      } = component;
      buildFormState(component, id);

      if (required) {
        checkDisabledSection(inputKey, index, id);
      }

      if (itemPropertiesCondition) {
        const { itemProperties = [] } = currentItem;
        const match = itemProperties.find(
          (property) => property.key === itemPropertiesCondition.key,
        );
        if (match.value !== itemPropertiesCondition.value) {
          return null;
        }
      }

      if (name === 'SidebarPanelLink') {
        return (
          <div
            key={componentKey}
            onClick={() => actions.switchPanel(panelLink)}
            className={s.panel_link}
          >
            {text}
          </div>
        );
      } else if (name === 'ButtonMenu') {
        const reasons = component.multipleSelect ? multipleSelectReasons : repairReasons;
        // TODO: Move multiselect copy to language JSON
        return (
          <div>
            {component.multipleSelect ? <span>Please select all that apply.</span> : null}
            <ButtonMenu
              key={inputKey}
              menuItems={buttonMenuItems(inputKey, component.multipleSelect)}
              itemKey="reason_code_id"
              propName="display_name"
              formKey={inputKey}
              itemClick={(reasonIndex) => {
                if (reasonSelected(reasons[reasonIndex], 'display_name', inputKey)) {
                  actions.resetReason(reasonIndex, inputKey);
                } else {
                  actions.selectReason(reasonIndex, inputKey, component.multipleSelect);
                }
              }}
              checkSelected={reasonSelected}
              multipleSelect={component.multipleSelect}
            />
          </div>
        );
      } else if (name === 'SidebarHtmlContent') {
        return <SidebarHTMLContent key={componentKey} html={content} centerContent={center} />;
      } else if (name === 'TextArea') {
        return (
          <RepairTextAreaComponent
            textAreaValue={currentItem[inputKey]}
            key={componentKey}
            inputKey={inputKey}
            component={component}
            maxCommentLength={maxCommentLength}
            onTextChange={(data) => {
              actions.formChange(data);
            }}
          />
        );
      } else if (name === 'ImageUploader') {
        const handleUploadImage = () => actions.uploadFile(inputKey);
        const handleDeleteImage = () => actions.deletePhoto(inputKey);

        return (
          <div>
            <Alert
              show={Boolean(currentItem.imageUploadErrorTooltip)}
              dismissible
              dismissOnClick={actions.resetImageErrorMsg}
              s={{
                padding: '15px',
                borderStyle: 'none',
                backgroundColor: '#e53e3a',
                color: '#f3f3f3',
              }}
            >
              {currentItem.imageUploadErrorTooltip}
            </Alert>

            <input
              id="fileInput"
              type="file"
              accept=".png, .jpg, .jpeg"
              onChange={handleUploadImage}
              ref={(node) => (customerFileUpload = node)}
              style={{ display: 'none' }}
            />
            <label
              id="fileInputUploadLabel"
              htmlFor="fileInput"
              style={{
                marginTop: '1rem',
                padding: '15px',
                border: '1px solid black',
                width: '100%',
                cursor: 'pointer',
                display: !currentItem.showRepairImage ? 'block' : 'none',
              }}
            >
              Choose Files
            </label>

            <div
              id="imageThumbnailContainer"
              className="imagePreview"
              style={{
                display: currentItem.showRepairImage ? 'block' : 'none',
                padding: '15px',
                border: '1px solid black',
                height: '100px',
              }}
            >
              <img
                id="imageThumbnail"
                src={currentItem.repairImageUrl}
                style={{ width: '70px', height: '70px', float: 'left' }}
              />
              <span style={{ float: 'right', padding: '25px 0 0 0' }}>
                <span style={{ padding: '10px', cursor: 'pointer' }} onClick={retakePhoto}>
                  <EditIcon />
                </span>
                <span style={{ padding: '10px', cursor: 'pointer' }} onClick={handleDeleteImage}>
                  <DeleteIcon />
                </span>
              </span>
            </div>
          </div>
        );
      }

      return null;
    });

    return (
      <div>
        {titleMarkup}
        {disabled ? null : componentsMarkup}
      </div>
    );
  };

  render(
    <div role="dialog" aria-label="Repair Sidebar">
      <ReturnsSidebar
        title={currentPanel.title}
        disableClose={maxItemsReached}
        toggleSide="right"
        sidebarOpen={open}
        backIconOnClick={panel ? () => actions.switchPanel(0) : null}
        closeIconOnClick={() => actions.toggle()}
        overlayOnClick={() => actions.toggle()}
      >
        {loading ? (
          <Spinner style={{ width: '4rem' }} />
        ) : (
          <List className={s.content_list} slideDirection="right">
            <div key={currentPanel.title}>
              {panel === 0 ? (
                <SidebarItemDetails
                  item={currentItem}
                  itemProperties={sidebarAttrs || ['Color', 'Size']}
                  language={language}
                />
              ) : null}

              {currentPanel.infoSectionComponents ? (
                <SidebarSection components={currentPanel.infoSectionComponents} />
              ) : null}

              {currentPanel.sections.map((section, index) => {
                const { title, components, condition, hidden, id } = section;
                const renderedSectionIndex = index - unrenderedSectionCount;
                let hideSection = hidden || false;

                // Override the default hidden value if this section should be dynamically displayed
                if (dynamicSections && id) {
                  hideSection = !dynamicSections.includes(id);
                }

                if ((condition && condition !== itemCategory) || hideSection) {
                  unrenderedSectionCount += 1;
                  return null;
                }

                return (
                  <SidebarSection
                    id={id}
                    key={title}
                    title={title}
                    index={renderedSectionIndex}
                    components={components}
                    disabled={disableSections && renderedSectionIndex > lastEnabledSection}
                  />
                );
              })}

              {panel === 0 ? (
                <div>
                  <hr className={s.divider} />
                  <Button
                    className="narvar-btn-primary sidebar-done-button sidebar-action-btn"
                    big
                    disabled={checkRequiredFields() || commentEmpty}
                    onClick={() => actions.finalizeItem()}
                  >
                    {maxItemsReached
                      ? language['returns.step_1.sidepanel_continue']
                      : language['returns.step_1.sidepanel_next']}
                  </Button>

                  {!checkRequiredFields() && !commentEmpty && !additionalItemsAvailable ? (
                    <Button
                      className="narvar-btn-secondary sidebar-additional-items-button sidebar-action-btn"
                      s={{ marginTop: '1rem', ...outline() }}
                      big
                      onClick={() => actions.finalizeItem(true)}
                    >
                      {language['returns.step_1.sidepanel_additional_items']}
                    </Button>
                  ) : null}

                  {savedItem && savedItem.selected ? (
                    <Button
                      className="narvar-btn-secondary sidebar-cancel-item-button sidebar-action-btn"
                      onClick={actions.deleteItem}
                      s={{ marginTop: '1rem' }}
                      big
                    >
                      {language['returns.step_1.sidepanel_dont_return']}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </List>
        )}
      </ReturnsSidebar>
    </div>,
    node,
  );
};
